import {
  Dimension,
  MetricPoint,
  MetricSeries,
} from "../../../domain/models/metrics";

export enum ActivityLineChartDataKey {
  Date = "date",
  TickLabel = "tickLabel",
  PeriodStart = "periodStart",
  PeriodEnd = "periodEnd",
  HoursInVr = "hoursInVr",
  UserSessions = "userSessions",
  UniqueActiveUsers = "uniqueActiveUsers",
}

export interface ActivityLineChartDataPoint {
  [ActivityLineChartDataKey.Date]: string;
  [ActivityLineChartDataKey.TickLabel]: string;
  [ActivityLineChartDataKey.PeriodStart]: Date;
  [ActivityLineChartDataKey.PeriodEnd]: Date;
  [ActivityLineChartDataKey.HoursInVr]: number;
  [ActivityLineChartDataKey.UserSessions]: number;
  [ActivityLineChartDataKey.UniqueActiveUsers]: number;
}

export interface UseChartDataResult {
  chartData: ActivityLineChartDataPoint[];
  legendValues: {
    hoursInVr: number;
    uniqueActiveUsers: number;
    userSessions: number;
  };
  isLoading: boolean;
  isFetching: boolean;
}

export const roundToOneDecimalPlace = (value: number): number =>
  Math.round(value * 10) / 10;

export const populateDataPointsMap = (
  dataPoints: MetricPoint[] | undefined,
  propertyKey: Exclude<
    ActivityLineChartDataKey,
    | ActivityLineChartDataKey.Date
    | ActivityLineChartDataKey.PeriodEnd
    | ActivityLineChartDataKey.PeriodStart
    | ActivityLineChartDataKey.TickLabel
  >,
  dataPointsMap: Map<string, ActivityLineChartDataPoint>,
  getKey: (date: Date) => string,
): void => {
  if (!dataPoints) return;

  dataPoints.forEach((dp: MetricPoint) => {
    const key = getKey(dp.periodStart);
    const existing = dataPointsMap.get(key) ?? {
      [ActivityLineChartDataKey.Date]: key,
      [ActivityLineChartDataKey.TickLabel]: key,
      [ActivityLineChartDataKey.PeriodStart]: new Date(0),
      [ActivityLineChartDataKey.PeriodEnd]: new Date(0),
      [ActivityLineChartDataKey.HoursInVr]: 0,
      [ActivityLineChartDataKey.UserSessions]: 0,
      [ActivityLineChartDataKey.UniqueActiveUsers]: 0,
    };

    existing[propertyKey] += dp.value;
    dataPointsMap.set(key, existing);
  });
};

const MS_IN_DAY = 24 * 60 * 60 * 1000;

const getWeekNumber = (date: Date): number => {
  const d = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));

  return Math.ceil(((d.getTime() - yearStart.getTime()) / MS_IN_DAY + 1) / 7);
};

// Returns the ISO week-based year, which can differ from the calendar year at year boundaries (e.g. Dec 29 → W01 of next year).
const getISOWeekYear = (date: Date): number => {
  const d = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);

  return d.getUTCFullYear();
};

const getQuarter = (date: Date): number =>
  Math.floor(date.getUTCMonth() / 3) + 1;

const getPeriodEnd = (start: Date, dimension: Dimension): Date => {
  const end = new Date(start);

  switch (dimension) {
    case Dimension.WEEK: {
      const isoDayOfWeek = end.getUTCDay() || 7;
      end.setUTCDate(end.getUTCDate() + (7 - isoDayOfWeek));
      break;
    }
    case Dimension.MONTH:
      end.setUTCMonth(end.getUTCMonth() + 1, 0);
      break;
    case Dimension.QUARTER:
      end.setUTCMonth(getQuarter(end) * 3, 0);
      break;
    case Dimension.YEAR:
      end.setUTCMonth(11, 31);
      break;
  }

  return end;
};

export const formatDateByDimension = (
  date: Date,
  dimension: Dimension,
): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const yearLabel = `\n${year}`;

  switch (dimension) {
    case Dimension.WEEK: {
      const week = String(getWeekNumber(date)).padStart(2, "0");
      const isoYear = getISOWeekYear(date);
      const isoYearLabel = `\n${isoYear}`;

      return `W${week}${isoYearLabel}`;
    }
    case Dimension.MONTH:
      return `${month}${yearLabel}`;
    case Dimension.QUARTER: {
      const quarter = getQuarter(date);

      return `Q${quarter}${yearLabel}`;
    }
    case Dimension.YEAR:
      return `${year}`;
    default:
      return `${month}${yearLabel}`;
  }
};

export const getXAxisTicks = (
  startDate: Date,
  endDate: Date,
  dimension: Dimension,
): Array<{
  key: string;
  label: string;
  periodStart: Date;
  periodEnd: Date;
}> => {
  const ticks: Array<{
    key: string;
    label: string;
    periodStart: Date;
    periodEnd: Date;
  }> = [];
  const current = new Date(startDate);

  while (current <= endDate) {
    const key = formatDateByDimension(current, dimension);
    const periodStart = new Date(current);
    const periodEnd = getPeriodEnd(current, dimension);

    ticks.push({ key, label: key, periodStart, periodEnd });

    current.setTime(periodEnd.getTime());
    current.setUTCDate(current.getUTCDate() + 1);
  }

  return ticks;
};

export const buildActivityChartData = (
  startDate: Date,
  endDate: Date,
  dimension: Dimension,
  hoursInVrData: MetricSeries | undefined,
  uniqueActiveUserData: MetricSeries | undefined,
  userSessionData: MetricSeries | undefined,
): ActivityLineChartDataPoint[] => {
  const xAxisTicks = getXAxisTicks(startDate, endDate, dimension);
  const dataPointsByUnitTime = new Map<string, ActivityLineChartDataPoint>();
  const getKey = (date: Date) => formatDateByDimension(date, dimension);

  populateDataPointsMap(
    hoursInVrData?.points,
    ActivityLineChartDataKey.HoursInVr,
    dataPointsByUnitTime,
    getKey,
  );
  populateDataPointsMap(
    uniqueActiveUserData?.points,
    ActivityLineChartDataKey.UniqueActiveUsers,
    dataPointsByUnitTime,
    getKey,
  );
  populateDataPointsMap(
    userSessionData?.points,
    ActivityLineChartDataKey.UserSessions,
    dataPointsByUnitTime,
    getKey,
  );

  return xAxisTicks.map(({ key, label, periodStart, periodEnd }) => {
    const dataPoint =
      dataPointsByUnitTime.get(key) ?? generateEmptyDataPoint(key);

    return {
      ...dataPoint,
      [ActivityLineChartDataKey.Date]: key,
      [ActivityLineChartDataKey.TickLabel]: label,
      [ActivityLineChartDataKey.PeriodStart]: periodStart,
      [ActivityLineChartDataKey.PeriodEnd]: periodEnd,
      [ActivityLineChartDataKey.HoursInVr]: roundToOneDecimalPlace(
        dataPoint[ActivityLineChartDataKey.HoursInVr],
      ),
    };
  });
};

export const buildLegendValues = (
  hoursInVrData: MetricSeries | undefined,
  uniqueActiveUserData: MetricSeries | undefined,
  userSessionData: MetricSeries | undefined,
): UseChartDataResult["legendValues"] => ({
  hoursInVr: hoursInVrData?.aggregateValue ?? 0,
  uniqueActiveUsers: uniqueActiveUserData?.aggregateValue ?? 0,
  userSessions: userSessionData?.aggregateValue ?? 0,
});

export const generateEmptyDataPoint = (
  date: string,
): ActivityLineChartDataPoint => ({
  [ActivityLineChartDataKey.Date]: date,
  [ActivityLineChartDataKey.TickLabel]: date,
  [ActivityLineChartDataKey.PeriodStart]: new Date(0),
  [ActivityLineChartDataKey.PeriodEnd]: new Date(0),
  [ActivityLineChartDataKey.HoursInVr]: 0,
  [ActivityLineChartDataKey.UserSessions]: 0,
  [ActivityLineChartDataKey.UniqueActiveUsers]: 0,
});
